<template>
  <div>
    <b-modal
      v-model="visibleLocal"
      size="xl"
      title="Upload Excel"
      hide-footer
      no-close-on-backdrop
      @hidden="onHidden"
    >
      <div v-if="!confirmMode">
        <b-form-group
          label="Select Excel File"
          label-for="file-upload"
          description="Supported formats: .xlsx, .xls"
        >
          <b-form-file
            v-model="file"
            id="file-upload"
            accept=".xlsx, .xls"
            placeholder="Choose a file or drop it here..."
            drop-placeholder="Drop file here..."
            @change="handleFileUpload"
          ></b-form-file>
        </b-form-group>
        <div v-if="loading" class="text-center my-4">
            <b-spinner label="Loading..."></b-spinner>
            <p>Reading file...</p>
        </div>
      </div>

      <div v-if="confirmMode">
        <b-row class="mb-3">
          <b-col>
             <h5>Preview Data</h5>
             <p class="text-muted">Total Records: <strong>{{ previewData.length }}</strong></p>
          </b-col>
           <b-col class="text-right">
             <b-button variant="outline-secondary" @click="reset">Cancel</b-button>
             <b-button variant="primary" @click="processUpload" :disabled="uploading">
               <span v-if="uploading"><b-spinner small></b-spinner> Processing... {{ progress }}%</span>
               <span v-else>Start Upload</span>
             </b-button>
           </b-col>
        </b-row>
        
        <b-progress v-if="uploading" :value="progress" show-progress animated class="mb-3"></b-progress>

         <div class="table-responsive" style="max-height: 500px">
            <table class="table table-sm table-striped table-bordered">
                <thead>
                    <tr>
                        <th v-for="(val, key) in previewData[0]" :key="key">{{ key }}</th>
                        <th style="width: 100px">Status</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, index) in paginatedData" :key="index">
                        <td v-for="(val, key) in row" :key="key">
                             <span v-if="key !== '_status' && key !== '_error'">{{ val }}</span>
                        </td>
                         <td>
                            <b-badge v-if="row._status === 'success'" variant="success">Success</b-badge>
                            <b-badge v-else-if="row._status === 'error'" variant="danger" :title="row._error">Error</b-badge>
                            <b-badge v-else variant="secondary">Pending</b-badge>
                        </td>
                    </tr>
                </tbody>
            </table>
         </div>
         <p class="text-center text-muted mt-2" v-if="previewData.length === 0">No data to display.</p>
         
         <div v-else>
           <b-pagination
              v-model="currentPage"
              :total-rows="previewData.length"
              :per-page="perPage"
              aria-controls="my-table"
              align="center"
              class="mt-3"
            ></b-pagination>
             <p class="text-center text-muted small">
                Showing {{ ((currentPage - 1) * perPage) + 1 }} to {{ Math.min(currentPage * perPage, previewData.length) }} of {{ previewData.length }} entries
            </p>
         </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import * as XLSX from "xlsx";

export default {
  name: "ExcelImporter",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    mapData: {
      type: Function,
      required: true,
      // Function to transform array of arrays or array of objects to desired object format
    },
    uploadFunction: {
      type: Function,
      required: true,
      // Async function that takes a single row item and returns a promise
    },
    batchUpload: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      visibleLocal: this.visible,
      file: null,
      loading: false,
      confirmMode: false,
      previewData: [],
      uploading: false,
      progress: 0,
      currentPage: 1,
      perPage: 10,
    };
  },
  watch: {
    visible(val) {
      this.visibleLocal = val;
      if (val) {
          this.reset();
      }
    },
    visibleLocal(val) {
      this.$emit("update:visible", val);
      if (!val) {
        this.$emit("close");
      }
    },
  },
  computed: {
      paginatedData() {
          const start = (this.currentPage - 1) * this.perPage;
          const end = start + this.perPage;
          return this.previewData.slice(start, end);
      }
  },
  methods: {
    onHidden() {
        this.$emit("update:visible", false);
    },
    reset() {
      this.file = null;
      this.loading = false;
      this.confirmMode = false;
      this.previewData = [];
      this.uploading = false;
      this.progress = 0;
    },
    handleFileUpload(e) {
      const file = e.target.files ? e.target.files[0] : null; // Handle both event and model based changes
      if (!file) return;

      this.loading = true;
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            
            // Get raw data as array of arrays to handle headers manually or use json
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            
            // Remove empty rows
            const cleanData = jsonData.filter(row => row && row.length > 0);

            if (cleanData.length < 2) {
                 alert("File appears to be empty or only has headers.");
                 this.reset();
                 return;
            }

            // Call the mapper provided by parent
            try {
                this.previewData = this.mapData(cleanData);
                 // Add status tracking fields
                this.previewData = this.previewData.map(item => ({...item, _status: 'pending', _error: null}));
                this.confirmMode = true;
            } catch (mapperError) {
                console.error("Mapping error:", mapperError);
                alert("Error mapping Excel data. Please check the file format.");
                this.reset();
            }

        } catch (error) {
            console.error("Error parsing Excel:", error);
            alert("Failed to parse Excel file.");
            this.reset();
        } finally {
            this.loading = false;
        }
      };

      reader.readAsArrayBuffer(file);
    },
    async processUpload() {
      if (this.uploading) return;
      this.uploading = true;
      const total = this.previewData.length;

      // --- Batch Upload Mode ---
      if (this.batchUpload) {
          try {
             // Prepare clean payload (remove internal status fields)
             const payload = this.previewData.map(item => {
                 const { _status, _error, ...data } = item;
                 return data;
             });

             await this.uploadFunction(payload);
             
             // Mark all as success if no error thrown
             this.previewData.forEach((item, i) => {
                 this.$set(this.previewData, i, { ...item, _status: 'success', _error: null });
             });
             
             this.progress = 100;
             this.$emit("done", { success: total, error: 0 });
             setTimeout(() => {
                alert(`Batch Upload Complete! ${total} records processed.`);
                this.visibleLocal = false;
             }, 500);

          } catch (err) {
              console.error("Batch upload error:", err);
              // Mark all as error or handle specifically if API returns index-based errors
              this.previewData.forEach((item, i) => {
                 this.$set(this.previewData, i, { ...item, _status: 'error', _error: err.message || "Batch failed" });
              });
              alert("Batch Upload Failed: " + (err.message || "Unknown Error"));
          } finally {
              this.uploading = false;
          }
          return;
      }

      // --- Row-by-Row Mode (Existing Logic) ---
      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < total; i++) {
        const item = this.previewData[i];
        
        // Skip already processed items if retrying
        if (item._status === 'success') {
             successCount++;
             continue;
        }

        try {
             // Create a clean copy without internal status fields
            const { _status, _error, ...payload } = item; 
            await this.uploadFunction(payload);
            item._status = 'success';
            item._error = null;
            successCount++;
        } catch (err) {
            console.error("Upload error for row " + i, err);
            item._status = 'error';
            item._error = err.message || "Upload failed";
            errorCount++;
        }
        
        this.$set(this.previewData, i, item);
        this.progress = Math.round(((i + 1) / total) * 100);
      }

      this.uploading = false;
      this.$emit("done", { success: successCount, error: errorCount });
      
      if (errorCount === 0) {
           setTimeout(() => {
               alert(`Upload Complete! ${successCount} records imported.`);
               this.visibleLocal = false;
           }, 500);
      } else {
          alert(`Upload Complete with errors. Success: ${successCount}, Failed: ${errorCount}. Please check the list.`);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.table-responsive {
    display: block;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}
</style>
